import React, { useState, useMemo, useEffect } from "react";
import "./Cohorts.css";
import { IoIosSearch } from "react-icons/io";
import { Link, useNavigate } from "react-router-dom";
import { BiCategory } from "react-icons/bi";
import { FaCalendarAlt, FaClock, FaDollarSign, FaMapMarkerAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { fetchCohorts } from "../../redux/slices/frontend/cohortSlice";
import CourseSVG from "./Pencil";
import { detectUserCountry } from "../../utils/detectCountry";
import { useAuth } from "../../contexts/AuthContext";
import { fetchUserEnrolledCohorts } from "../../redux/slices/userEnrolledCohortSlice";
import { DollarSign } from "lucide-react";

function Cohorts() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuth();
  const { enrollment: userEnrollments } = useSelector((state) => state.userEnrolledCohorts);
  
  const isPrivateStudent = isAuthenticated && (
    user?.role === 'private_student' || 
    userEnrollments?.some(e => e.class_option === 'Private Class')
  );

  const { cohorts, loading, error } = useSelector((state) => state.cohorts);
  const [searchTerm, setSearchTerm] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [countryDetected, setCountryDetected] = useState(false);
  // Exchange rate state
  const [usdToNgn, setUsdToNgn] = useState(null);
  const [rateLoading, setRateLoading] = useState(true);

  useEffect(() => {
    // Auto-detect visitor's country and set currency accordingly
    const autoDetectCurrency = async () => {
      const { currency: detectedCurrency } = await detectUserCountry();
      setCurrency(detectedCurrency);
      setCountryDetected(true);
    };
    autoDetectCurrency();

    // Fetch exchange rate from provided Exchange Rate API
    const fetchRate = async () => {
      setRateLoading(true);
      try {
        const res = await fetch("https://v6.exchangerate-api.com/v6/689d396c0a109d335cc4092c/latest/USD");
        const data = await res.json();
        setUsdToNgn(data.conversion_rates?.NGN);
      } catch (e) {
        setUsdToNgn(null);
      } finally {
        setRateLoading(false);
      }
    };
    fetchRate();
  }, []);
  const [selectedFilters, setSelectedFilters] = useState({
    category: "",
    status: "",
  });


  useEffect(() => {
    dispatch(fetchCohorts());
    if (localStorage.getItem('authToken')) {
      dispatch(fetchUserEnrolledCohorts());
    }
  }, [dispatch]);

  // Filter cohorts based on search term and selected filters
  const filteredCohorts = useMemo(() => {
    if (!cohorts) return [];
    const result = cohorts.filter((course) => {
      if (course.status?.toLowerCase() === "completed") return false;

      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        course.course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.course.category.toLowerCase().includes(searchTerm.toLowerCase());
      // Category filter
      const matchesCategory =
        !selectedFilters.category ||
        course.course.category.toLowerCase() ===
        selectedFilters.category.toLowerCase();

      // Status filter
      const matchesStatus =
        !selectedFilters.status ||
        course.status.toLowerCase() === selectedFilters.status.toLowerCase();

      return matchesSearch && matchesCategory && matchesStatus;
    });
    // Sort the result by start date
    return result.sort((a, b) => {
      const dateA = new Date(a.start_date);
      const dateB = new Date(b.start_date);
      return dateA - dateB;
    });
  }, [cohorts, searchTerm, selectedFilters]);

  const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const truncateText = (text, maxLength = Infinity) => {
    const cleanText = stripHtml(text);
    return cleanText.length > maxLength
      ? cleanText.substring(0, maxLength) + "..."
      : cleanText;
  };

  const handleFilterChange = (type, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleRegisterCourse = (cohortId) => {
    const filteredCohorts = cohorts.find((cohort) => cohort.id === cohortId);

    setTimeout(() => {
      navigate("/cohort/register", {
        state: {
          cohort_id: cohortId,
          cohortName: filteredCohorts.course?.title,
          coursePrice: filteredCohorts.course?.price,
          cohortPrice: filteredCohorts.dollar_price
        },
      });
    }, 250);
  };

  return (
    <div>
      <section className="cohort-hero-section">
        <div className="hero-content">
          <h1>
            Training <span className="primary-color">Cohorts</span>
          </h1>
          <p>
            Unlock high-impact career growth. Explore our intensive programs
            designed to deliver immediate, in-demand technical authority in tech
            and data science.
          </p>
        </div>

        <CourseSVG />
      </section>

      <section className="cohort-section">
        <div className="cohort-title">
          <h2>Available Training Programs</h2>
        </div>

        <div className="search-filter-container">
          <div className="search-section">
            <IoIosSearch className="search-icon" />
            <input
              className="cohort-search-input"
              placeholder="Search by title or category.."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filters-section">
            <div className="filter-group">
              <label className="filter-label">Category:</label>
              <select
                className="filter-dropdown"
                value={selectedFilters.category || ""}
                onChange={(e) => handleFilterChange("category", e.target.value)}
              >
                <option value="">All Categories</option>
                <option value="Data Science">Data Science</option>
                <option value="Machine Learning">Machine Learning</option>
                <option value="Web Development">Web Development</option>
                <option value="AI">AI</option>
                <option value="Data Analytics">Data Analytics</option>
                <option value="Programming">Programming</option>
                <option value="Mobile Development">Mobile Development</option>
                <option value="Design">Design</option>
                <option value="Business">Business</option>
                <option value="Marketing">Marketing</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Status:</label>
              <select
                className="filter-dropdown"
                value={selectedFilters.status || ""}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Cohorts</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="upcoming">Upcoming</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                Currency:
                {!countryDetected && (
                  <span style={{ fontSize: "0.75rem", color: "#888", marginLeft: "6px" }}>
                    Detecting…
                  </span>
                )}
              </label>
              <div className="currency-toggle">
                <button
                  className={`currency-btn ${currency === "NGN" ? "active" : ""}`}
                  onClick={() => setCurrency("NGN")}
                  type="button"
                >
                  ₦ NGN
                </button>
                <button
                  className={`currency-btn ${currency === "USD" ? "active" : ""}`}
                  onClick={() => setCurrency("USD")}
                  type="button"
                >
                  $ USD
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="courses-container">
          <div className="side-filter">
            {/* Category Filter */}
            <div className="filter-group">
              <label className="filter-label">Category</label>
              <ul className="filter-list">
                {[
                  "",
                  "Machine Learning",
                  "Web Development",
                  "AI",
                  "Data Science",
                  "Data Analytics",
                  "Programming",
                  "Mobile Development",
                  "Design",
                  "Business",
                  "Marketing",
                ].map((category) => (
                  <li key={category}>
                    <label className="filter-item">
                      <input
                        type="checkbox"
                        value={category}
                        checked={
                          selectedFilters.category.toLowerCase() ===
                          category.toLowerCase()
                        }
                        onChange={(e) =>
                          handleFilterChange("category", e.target.value)
                        }
                      />
                      <span>{category === "" ? "All Categories" : category}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            <div className="filter-group">
              <label className="filter-label">Status</label>
              <ul className="filter-list">
                {["", "Active", "Inactive", "Upcoming"].map((status) => (
                  <li key={status}>
                    <label className="filter-item">
                      <input
                        type="checkbox"
                        value={status}
                        checked={
                          selectedFilters.status === status.toLowerCase()
                        }
                        onChange={(e) =>
                          handleFilterChange("status", e.target.value)
                        }
                      />
                      <span>{status === "" ? "All Status" : status}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            {loading && (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading Cohorts...</p>
              </div>
            )}

            {error && (
              <div className="error-container">
                <p className="error-message">Error: {error}</p>
              </div>
            )}

            {!loading && !error && (
              <div className="courses-grid">
                {filteredCohorts.length > 0 ? (
                  filteredCohorts.map((cohort) => {
                    return (
                      <article key={cohort.id} className="course-card">
                        <div className="course-card-header">
                          <div
                            className="course-image"
                            style={{
                              backgroundImage: cohort.course.image
                                ? `url(${cohort.course.image})`
                                : "linear-gradient(135deg, #0a74da 0%, #487efc 100%)",
                            }}
                          >
                            <div className="course-overlay">
                              <span className="category-badge">
                                <BiCategory />
                                {cohort.course.category}
                              </span>
                              <span className={`status-badge ${cohort.status}`}>
                                {cohort.status?.toLowerCase() === "active" ? "Registration Ongoing" : cohort.status?.toLowerCase() === "inactive" ? "Coming Soon" : cohort.status}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="course-card-body">
                          <h3 className="course-title">
                            {cohort.name}
                          </h3>
                          <div className="course-metadata">
                            <p className="course-description-text mb-4">
                              {stripHtml(cohort.course.description, 310)}
                            </p>

                            <div className="course-metadata-pills">
                              {cohort.course?.location && (
                                <div className="metadata-pill">
                                  <FaMapMarkerAlt className="metadata-pill-icon text-primary" />
                                  <span className="metadata-pill-label">Location:</span>
                                  <span className="metadata-pill-value">{cohort.course.location}</span>
                                </div>
                              )}
                              <div className="metadata-pill">
                                <FaCalendarAlt className="metadata-pill-icon" />
                                <span className="metadata-pill-label">Starts:</span>
                                <span className="metadata-pill-value">
                                  {new Date(cohort.start_date).toLocaleDateString(undefined, {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric"
                                  })}
                                </span>
                              </div>

                              <div className="metadata-pill">
                                <FaClock className="metadata-pill-icon" />
                                <span className="metadata-pill-label">Duration:</span>
                                <span className="metadata-pill-value">{cohort.duration}</span>
                              </div>

                              <div className="metadata-pill highlight-price">
                                {currency === "USD" ? (
                                  <>
                                    <span className="metadata-pill-label">Price:</span>
                                    <span className="metadata-pill-value price-tag">
                                    <DollarSign size={15} className="metadata-pill-icon text-primary" />
                                      {cohort.dollar_price
                                        ? parseFloat(cohort.dollar_price).toLocaleString(undefined, {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0
                                          })
                                        : "N/A"}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="metadata-pill-label">Price:</span>
                                    <span className="metadata-pill-value price-tag">
                                    <span className="metadata-pill-icon font-weight-bold text-primary">₦</span>
                                      {rateLoading ? (
                                        "Loading..."
                                      ) : usdToNgn && cohort.dollar_price ? (
                                        (parseFloat(cohort.dollar_price) * usdToNgn).toLocaleString(undefined, {
                                          maximumFractionDigits: 0
                                        })
                                      ) : (
                                        "N/A"
                                      )}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="course-actions">
                            <button
                              onClick={() => handleRegisterCourse(cohort?.id)}
                              className="primary-btn"
                            >
                              Enroll Now
                            </button>
                            <Link to={`/cohorts/${cohort.slug}/details`}>
                              <button className="details-btn secondary-btn">
                                View Details
                              </button>
                            </Link>
                          </div>
                        </div>
                      </article>
                    );
                  })
                ) : (
                  <div className="no-courses">
                    <h3>No cohort found</h3>
                    <p>Try adjusting your search or filter criteria.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Cohorts;
